import { clsx } from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { Router, useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { listingInfiniteScrollActiveAtom } from '@/atoms/listing-infinite-scroll';
import Footer from '@/components/Footer';
import IssueBanner from '@/components/IssueBanner';
import PageDialogQueue from '@/components/PageDialogQueue';
import PagePopupQueue from '@/components/PagePopupQueue';
import TopBar from '@/components/TopBar';
import { UBAlerts } from '@/components/UBAlerts';
import AppSidebar from '@/components/AppSidebar';
import { ShadcnSidebarAdapter } from '@/components/AppSidebar/ShadcnSidebarAdapter';
import { Banner } from '@/components/ui/banner';
import { Button } from '@music-vine/cadence/ui';
import UpsellModal from '@/components/UpsellModal';
import { hasCurrentTrackAtom, isPlayingAtom, pageHasPlayableAudioAtom, playbackControlAtom } from '@/features/AudioPlayer/atoms';
import { LazyAudioPlayer, preloadAudioPlayer } from '@/features/AudioPlayer/lazy';
import { useIsAdmin } from '@/hooks/auth';
import { useQueryChangeProgressBar } from '@/hooks/navigation/useQueryChangeProgressBar';
import { useSettings } from '@/hooks/state/useSettings';
import { useAuthToken } from '@/hooks/state/useAuthToken';
import { useOverduePayment } from '@/hooks/billing/useOverduePayment';
import { useReferralAlerts } from '@/hooks/useReferralAlerts';
import { useShowMoreVariant } from '@/hooks/useShowMoreVariant';
import { useThemeKeyboardShortcut } from '@/hooks/ui/useThemeKeyboardShortcut';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { logEvent } from '@/pages/api/v1/utils';
import { asPath } from '@/utils/router';
import { MainLayout } from './MainLayout';
import type { BannerRef } from '@/components/ui/banner';
import type { ReactNode } from 'react';
import { BoardsPopupContainer } from '@/features/Boards/popup-container ';
import { OverduePaymentBanner } from './banners/overdue-payment';
import { PromoCouponBanner } from './banners/promo-coupon';

// Admin-only, but its Modal Preview tool statically imports eleven modal
// bodies — so before DEV-6485 every visitor downloaded them. Only admins
// (and local dev) ever render it, so the whole toolbar is code-split.
const AdminBar = dynamic(() => import('@/components/AdminBar'), {
  ssr: false
});
interface AppContainerProps {
  children: ReactNode;
}
export const AppContainer = ({
  children
}: AppContainerProps) => {
  const router = useRouter();
  const bannerRef = useRef<BannerRef>(null);
  const {
    isSm
  } = useTWScreens();
  const isAdmin = useIsAdmin();
  const {
    settings
  } = useSettings();
  const isPremiumUser = !!settings?.isPremium;
  const isFree = asPath(router) === '/free';
  const isHomepage = router.pathname === '/';
  const isSubscribe = router.pathname.startsWith('/subscribe');

  // A listing page flips this on once it enters infinite-scroll mode with
  // more results still to fetch, and back off at the true end of the
  // results. We use it below to hide the site footer so the page commits to
  // the result stream.
  const isListingInfiniteScrollActive = useAtomValue(listingInfiniteScrollActiveAtom);
  const showMoreVariant = useShowMoreVariant();

  // Listing routes hide the footer wholesale today. In group B of the
  // show-more-infinite-scroll experiment the footer is instead gated by the
  // infinite-scroll atom above, so it renders until a listing enters
  // infinite mode and comes back at the true end of the results. Group A
  // (and flag-off / flag-missing) keeps the wholesale hide.
  const isListingRoute = router.pathname.includes('/browse/') || router.pathname.includes('/music/') || router.pathname.includes('/sfx/');
  const hideFooterOnListingRoute = isListingRoute && showMoreVariant === 'A';

  // Footer needs to be server-rendered for SEO and initial page structure,
  // so we don't wait for router.isReady here
  const shouldShowFooter = !(router.pathname.includes('/help-center') || hideFooterOnListingRoute || router.pathname.includes('/pricing') || router.pathname.includes('/subscribe') || isListingInfiniteScrollActive) || router.pathname === '/music/collection' || router.pathname === '/sfx/collection';
  const isPlaying = useAtomValue(isPlayingAtom);
  const controlPlayback = useSetAtom(playbackControlAtom);

  // The player is loaded lazily on first play intent (DEV-6487). Its atoms all
  // live in the main bundle and are unaffected — only the component code is
  // deferred. Once loaded we keep it mounted for the rest of the session so its
  // behaviour (including the empty state) is identical to before.
  const hasCurrentTrack = useAtomValue(hasCurrentTrackAtom);
  const pageHasPlayableAudio = useAtomValue(pageHasPlayableAudioAtom);
  const [audioPlayerLoaded, setAudioPlayerLoaded] = useState(false);
  useEffect(() => {
    if (hasCurrentTrack) {
      setAudioPlayerLoaded(true);
    }
  }, [hasCurrentTrack]);

  // Warm the chunk while the browser is idle as soon as the page has something
  // playable, so the click-to-audio path doesn't wait on a network request.
  // Pages with no audio never flip this, so they never fetch the chunk.
  useEffect(() => {
    if (!pageHasPlayableAudio || audioPlayerLoaded) {
      return;
    }
    if (typeof requestIdleCallback !== 'function') {
      preloadAudioPlayer();
      return;
    }
    const idleHandle = requestIdleCallback(() => preloadAudioPlayer());
    return () => cancelIdleCallback(idleHandle);
  }, [pageHasPlayableAudio, audioPlayerLoaded]);
  useReferralAlerts();

  // NOTE: Hide banner until further notice
  // const showUpgradeReferralBanner = useFeatureIsOn(
  //   'show-upgrade-referral-banner'
  // );
  const showUpgradeReferralBanner = false;
  useAuthToken();
  useThemeKeyboardShortcut();

  // Stop any music playback when on the subscribe page. The player UI is also
  // hidden below, but on Safari the audio element is created programmatically
  // and survives that unmount, so we must pause it explicitly. Guard on
  // isPlaying: playbackControlAtom calls createAudioElementForSafari() before
  // it checks for an existing element, so dispatching with nothing playing
  // would spawn a `new Audio()` outside a user gesture and break later Safari
  // playback.
  useEffect(() => {
    if (isSubscribe && isPlaying) {
      controlPlayback('pause');
    }
  }, [isSubscribe, isPlaying, controlPlayback]);
  useQueryChangeProgressBar();
  useEffect(() => {
    // Custom scroll restoration. Our pages fetch their content client-side, so
    // the document height grows *after* the route change. Neither the browser's
    // native restoration nor Next's experimental.scrollRestoration cope with
    // that — both scroll once, too early, and land near the top. So we own it:
    // persist the offset per history entry, scroll to top on forward nav, and
    // on back/forward re-apply the saved offset on a short rAF loop until the
    // async content is tall enough to reach it.
    const previousRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    const RESTORE_BUDGET_MS = 1500;
    const SAFETY_GRACE_MS = 500;
    const SCROLL_TOLERANCE_PX = 2;
    const storageKey = () => `scroll:${window.history.state?.key ?? window.location.pathname}`;

    // Block persisting transient offsets while the browser/Next scroll the page
    // during a pop (which would clobber the saved value before we restore it).
    let blockSave = false;
    let isPop = false;
    let saveRaf = 0;
    let safetyTimer = 0;
    const saveScroll = () => {
      if (blockSave) {
        return;
      }
      cancelAnimationFrame(saveRaf);
      saveRaf = requestAnimationFrame(() => {
        try {
          sessionStorage.setItem(storageKey(), String(window.scrollY));
        } catch {
          // sessionStorage unavailable (private mode / quota) — non-fatal
        }
      });
    };
    const restoreScroll = (y: number) => {
      blockSave = true;
      const start = performance.now();
      const tick = () => {
        window.scrollTo(0, y);
        const reached = Math.abs(window.scrollY - y) <= SCROLL_TOLERANCE_PX;
        if (reached || performance.now() - start >= RESTORE_BUDGET_MS) {
          blockSave = false;
        } else {
          requestAnimationFrame(tick);
        }
      };
      requestAnimationFrame(tick);
    };
    const onPopState = () => {
      isPop = true;
      blockSave = true;
      // Safety net: if no routeChangeComplete follows (e.g. hash-only change),
      // re-enable saving so we don't permanently freeze persistence.
      window.clearTimeout(safetyTimer);
      safetyTimer = window.setTimeout(() => {
        isPop = false;
        blockSave = false;
      }, RESTORE_BUDGET_MS + SAFETY_GRACE_MS);
    };
    const onRouteChangeComplete = (_url: string, {
      shallow
    }: {
      shallow?: boolean;
    }) => {
      window.clearTimeout(safetyTimer);
      // Preserve scroll on shallow (in-page query) navigation, e.g. filters.
      if (shallow) {
        return;
      }
      if (isPop) {
        isPop = false;
        let saved = 0;
        try {
          saved = Number(sessionStorage.getItem(storageKey())) || 0;
        } catch {
          saved = 0;
        }
        restoreScroll(saved);
      } else {
        blockSave = false;
        window.scrollTo(0, 0);
      }
    };
    window.addEventListener('scroll', saveScroll, {
      passive: true
    });
    window.addEventListener('popstate', onPopState);
    Router.events.on('routeChangeComplete', onRouteChangeComplete);
    return () => {
      cancelAnimationFrame(saveRaf);
      window.clearTimeout(safetyTimer);
      window.removeEventListener('scroll', saveScroll);
      window.removeEventListener('popstate', onPopState);
      Router.events.off('routeChangeComplete', onRouteChangeComplete);
      history.scrollRestoration = previousRestoration;
    };
  }, []);
  const logBannerClick = useCallback((type: string, popupId: string, nextPath: string) => {
    void logEvent({
      event_type: 'click',
      asPath: router.asPath,
      metadata: {
        type,
        popup_id: popupId,
        next_path: nextPath
      }
    });
  }, [router.asPath]);
  const {
    showBanner: showOverduePaymentBanner,
    overduePayment
  } = useOverduePayment();
  const {
    id: overduePaymentId
  } = overduePayment ?? {};
  const isHomePage = router.pathname === '/';
  const isOverdueBannerVisible = !!showOverduePaymentBanner && !!overduePaymentId && !isHomePage;
  return <MainLayout data-sentry-element="MainLayout" data-sentry-component="AppContainer" data-sentry-source-file="index.tsx">
      <UBAlerts data-sentry-element="UBAlerts" data-sentry-source-file="index.tsx" />
      <main className={clsx('main-content-section relative', !!isFree && 'w-full')}>
        <OverduePaymentBanner currentPath={router.asPath} overduePaymentId={overduePaymentId} visible={isOverdueBannerVisible} data-sentry-element="OverduePaymentBanner" data-sentry-source-file="index.tsx" />
        {!!showUpgradeReferralBanner && !isHomePage && <Banner condition={isPremiumUser && !overduePaymentId} currentPath={router.asPath} excludePaths={['/my/account/refer']} id="referral-premium-banner" onDismiss={() => {
        logBannerClick('dismiss', 'referral-premium-banner', router.asPath);
      }} ref={bannerRef} type="default" variant="top-sticky">
            <div className="flex grow flex-wrap items-center justify-between gap-2 sm:justify-center sm:gap-4">
              <span className="fl-text-sm/base">
                {isSm ? '📣 Refer your audience to Uppbeat & earn rewards!' : '📣 Earn rewards for referrals!'}
              </span>
              <Button asChild onClick={() => {
            bannerRef.current?.dismiss();
            logBannerClick('cta', 'referral-premium-banner', '/refer-and-earn');
          }} size="sm" variant="brand">
                <Link href="/refer-and-earn">Learn more</Link>
              </Button>
            </div>
          </Banner>}
        <PromoCouponBanner condition={!isOverdueBannerVisible} currentPath={router.asPath} data-sentry-element="PromoCouponBanner" data-sentry-source-file="index.tsx" />
        <IssueBanner data-sentry-element="IssueBanner" data-sentry-source-file="index.tsx" />
        <ShadcnSidebarAdapter data-sentry-element="ShadcnSidebarAdapter" data-sentry-source-file="index.tsx">
          <div className={clsx('main-layout-container App relative', isFree ? 'main-layout-fixed' : 'main-layout-grid')}>
            {!isSubscribe && <AppSidebar />}
            <div className={clsx('main-content-div @container relative overflow-x-clip', !!isHomepage && 'bg-white dark:bg-gray-900')} data-testid="main-content">
              {!isSubscribe && <TopBar />}
              {children}
              {!!shouldShowFooter && <div className="mt-16 md:mt-24">
                  <Footer />
                </div>}
            </div>
          </div>
        </ShadcnSidebarAdapter>
        {(process.env.NODE_ENV === 'development' || isAdmin) && <AdminBar />}
      </main>
      <PageDialogQueue data-sentry-element="PageDialogQueue" data-sentry-source-file="index.tsx" />
      <PagePopupQueue data-sentry-element="PagePopupQueue" data-sentry-source-file="index.tsx" />
      <BoardsPopupContainer data-sentry-element="BoardsPopupContainer" data-sentry-source-file="index.tsx" />
      {!isSubscribe && (hasCurrentTrack || audioPlayerLoaded) && <LazyAudioPlayer />}
      <UpsellModal data-sentry-element="UpsellModal" data-sentry-source-file="index.tsx" />
    </MainLayout>;
};